import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Container, Row, Col, Button, Spinner, Badge, Form } from "react-bootstrap";
import _ from "lodash";
import './curatedCandidates.css';
import { ClientFolderService } from "../../../../services/clientFolderService";
import { ClientService } from "../../../../services/clientService";
import { CandidatesTable } from "../../admin-candidate/candidate-listing-table/candidatesTables";
import { prepareCandidateData } from "../../../../utils/dataTableUtilsAdmin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { CuratedTables } from "./curatedTables";


const CuratedCandidates = ({ clientId, canManageCuration }) => {
    const [loading, setLoading] = useState(false);
    const [topFolders, setTopFolders] = useState([]);
    const [dropdownFolders, setDropdownFolders] = useState([]);
    const [folderPage, setFolderPage] = useState(0);
    const [folderPageCount, setFolderPageCount] = useState(0);
    const [folderPageSize] = useState(10);
    const [folders, setFolders] = useState([]);
    const [chosenFolderId, setChosenFolderId] = useState(null);
    const [tableRefresh, setTableRefresh] = useState(0);

    const [searchText, setSearchText] = useState("");
    const [committedSearch, setCommittedSearch] = useState("");

    const [isFetchingCandidates, setIsFetchingCandidates] = useState(false);

    const [totalEntries, setTotalEntries] = useState(0);
    const [startEntry, setStartEntry] = useState(0);
    const [endEntry, setEndEntry] = useState(0);

    const [showFolderList, setShowFolderList] = useState(false);
    const [showAllFolders, setShowAllFolders] = useState(false);
    const [showFolderActions, setShowFolderActions] = useState(null);
    const [showRenamePopup, setShowRenamePopup] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [folderToModify, setFolderToModify] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [activeFolderMenu, setActiveFolderMenu] = useState(null);
    const [folderToRename, setFolderToRename] = useState(null);
    const [folderToDelete, setFolderToDelete] = useState(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const viewAllRef = useRef(null);
    const folderActionsRef = useRef(null);
    const [showUncuratePopup, setShowUncuratePopup] = useState(false);
    const [candidateToUncurate, setCandidateToUncurate] = useState(null);
    const [uncurateLoading, setUncurateLoading] = useState(false);
    const [showUncurateSuccess, setShowUncurateSuccess] = useState(false);


    useEffect(() => {
        const handleClickOutside = (e) => {
            if (viewAllRef.current && !viewAllRef.current.contains(e.target)) {
                setShowAllFolders(false);
            }

            if (folderActionsRef.current && !folderActionsRef.current.contains(e.target)) {
                setShowFolderActions(null);
                setActiveFolderMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const nav = performance.getEntriesByType("navigation")[0];

        if (nav && nav.type !== "back_forward") {
            sessionStorage.removeItem("curated_state");
            return;
        }

        const saved = sessionStorage.getItem("curated_state");
        if (saved) {
            const parsed = JSON.parse(saved);

            if (parsed.chosenFolderId !== undefined) setChosenFolderId(parsed.chosenFolderId);
            if (parsed.searchText !== undefined) setSearchText(parsed.searchText);
            if (parsed.committedSearch !== undefined) setCommittedSearch(parsed.committedSearch);
            if (parsed.pageIndex !== undefined) setPageIndex(parsed.pageIndex);
            if (parsed.pageSize !== undefined) setPageSize(parsed.pageSize);
            if (parsed.showFolderList !== undefined) setShowFolderList(parsed.showFolderList);
            if (parsed.showAllFolders !== undefined) setShowAllFolders(parsed.showAllFolders);
        }
    }, []);

    const fetchFolders = useCallback(
        async (page = 0) => {
            if (!clientId) return;

            try {
                setLoading(true);

                const resp = await ClientFolderService.listCuratedFolders(
                    clientId,
                    "CURATED",
                    page,
                    folderPageSize
                );

                const folderList = (resp?.data?.folders || []).map((f) => ({
                    folderId: f.folderId,
                    name: f.name
                }));

                setFolderPageCount(resp?.data?.pageCount || 0);

                // FIRST API PAGE
                if (page === 0) {
                    // first 5 fixed tabs
                    setTopFolders(folderList.slice(0, 5));

                    // remaining 5 inside dropdown
                    setDropdownFolders(folderList.slice(5));
                } else {
                    // page 2,3,4...
                    // ALL entries go to dropdown only
                    setDropdownFolders(folderList);
                }

            } catch (err) {
                console.error("Error fetching curated folders", err);
            } finally {
                setLoading(false);
            }
        },
        [clientId, folderPageSize]
    );

    const lastRequestRef = useRef({});
    const lastResultRef = useRef(null);

    const fetchCuratedCandidates = useCallback(
        async (folderId, pi, ps, query) => {
            if (!clientId) {
                lastRequestRef.current = {};
                lastResultRef.current = null;

                return {
                    rows: { columns: [], data: [] },
                    pageCount: 0,
                };
            }

            const req = { folderId, pi, ps, query };
            const same = JSON.stringify(req) === JSON.stringify(lastRequestRef.current);

            if (same && lastResultRef.current) {
                return lastResultRef.current;
            }

            lastRequestRef.current = req;

            try {
                setIsFetchingCandidates(true);

                const resp = await ClientFolderService.getCuratedCandidates(
                    clientId,
                    folderId ?? "",
                    pi,
                    ps,
                    query
                );

                const curatedList = _.get(resp, "curatedList", []);

                const mapped = curatedList.map((item) => {
                    const rawCandidate = _.get(item, "candidate", {});
                    const candidateData = _.get(item, "candidate.candidate", {});
                    const candidateId = _.get(item, "candidate.candidateId", null);

                    const personal = _.get(candidateData, "personalDetails", {});
                    const workExp = _.get(candidateData, "workExperience.currentRole", {});

                    const fullName =
                        `${personal.firstName || ""} ${personal.lastName || ""}`.trim();

                    const displayPicPath =
                        personal?.displayPic?.[0]?.path ||
                        candidateData?.imageUrl ||
                        "";

                    const folder = _.get(item, "folder", null);

                    return {
                        ...candidateData,
                        candidateId,
                        fullName,
                        candidate: {
                            ...candidateData,
                            personalDetails: { ...personal },
                            workExperience: {
                                ...candidateData.workExperience,
                                currentRole: { ...workExp },
                            },
                        },
                        imageUrl: displayPicPath,
                        folderName: folder?.name || null,
                        folderId: folder?.folderId ?? null,
                        shortlistStatus: _.get(item, "shortlistStatus", ""),
                        shortlistedDate: _.get(item, "shortlistedDate", null),
                        assignedTo: rawCandidate.assignedTo || null,
                        status: rawCandidate.status || "",
                        searchStatus: item?.searchStatus || rawCandidate?.searchStatus || "",
                    };
                });


                const totalCount = resp.totalCount || 0;
                const paginated = mapped;
                const pageCount = Math.max(1, Math.ceil(totalCount / ps));

                const tableData = prepareCandidateData(
                    { candidates: paginated },
                    null,
                    false,
                    false,
                    false,
                    true
                );
                setTotalEntries(totalCount);
                setStartEntry(totalCount === 0 ? 0 : pi * ps + 1);
                setEndEntry(Math.min((pi + 1) * ps, totalCount));

                const result = {
                    rows: { columns: tableData.columns, data: tableData.data },
                    pageCount,
                };

                lastResultRef.current = result;

                return result;
            } catch (err) {
                console.error("Error fetching curated candidates", err);

                const fallback = {
                    rows: { columns: [], data: [] },
                    pageCount: 0,
                };

                lastResultRef.current = fallback;

                setTotalEntries(0);
                setStartEntry(0);
                setEndEntry(0);

                return fallback;
            } finally {
                setIsFetchingCandidates(false);
            }
        },
        [clientId]
    );

    useEffect(() => {
        if (!clientId) return;
        fetchFolders(0)
    }, [clientId, fetchFolders]);

    useEffect(() => {
        window.onUncurateCandidateClick = (candidateId, folderId) => {
            setCandidateToUncurate({ candidateId, folderId });
            setShowUncuratePopup(true);
        };

        return () => {
            try {
                delete window.onUncurateCandidateClick;
            } catch (e) { }
        };
    }, []);

    const handleUncurateConfirm = async () => {
        if (!candidateToUncurate?.candidateId || !candidateToUncurate?.folderId || !clientId) return;

        try {
            setUncurateLoading(true);

            await ClientService.uncurateCandidate(
                clientId,
                candidateToUncurate.candidateId,
                candidateToUncurate.folderId
            );
            lastRequestRef.current = {};
            lastResultRef.current = null;
            setTableRefresh((prev) => prev + 1);
            setShowUncuratePopup(false);
            setCandidateToUncurate(null);
            setShowUncurateSuccess(true);

        } catch (err) {
            console.error("Uncurate failed", err);
        } finally {
            setUncurateLoading(false);
        }
    };

    const tableFetchData = useCallback(
        async (opts) => {
            const {
                pageIndex: pi,
                pageSize: ps,
                chosenFolderId,
                committedSearch
            } = opts;

            return fetchCuratedCandidates(
                chosenFolderId ?? null,
                pi,
                ps,
                committedSearch ?? ""
            );
        },
        [fetchCuratedCandidates]
    );



    useEffect(() => {
        setStartEntry(totalEntries === 0 ? 0 : pageIndex * pageSize + 1);
        setEndEntry(Math.min((pageIndex + 1) * pageSize, totalEntries));
    }, [pageIndex, pageSize, totalEntries]);


    // const displayedFolders = folders.slice(0, 6);
    // const additionalFolders = folders.slice(displayedFolders.length || 0);
    const displayedFolders = topFolders;
    const additionalFolders = dropdownFolders;


    const runSearchCommit = async (value) => {
        const trimmed = (value || "").trim();
        setCommittedSearch(trimmed);
        setPageIndex(0);

    };

    useEffect(() => {
        if (searchText === "" && committedSearch !== "") {
            setCommittedSearch("");
            setPageIndex(0);
            fetchCuratedCandidates(chosenFolderId, 0, pageSize, "");
        }
    }, [searchText, committedSearch, chosenFolderId, pageSize, fetchCuratedCandidates]);

    const openRenamePopup = (folder) => {
        setActiveFolderMenu(null);
        setFolderToRename(folder);
        setRenameValue(folder.name);

        setShowRenamePopup(true);
    };

    const openDeleteConfirm = (folder) => {
        setActiveFolderMenu(null);
        setFolderToDelete(folder);
        setShowDeletePopup(true);
    };

    useEffect(() => {
        sessionStorage.setItem(
            "curated_state",
            JSON.stringify({
                chosenFolderId,
                searchText,
                committedSearch,
                pageIndex,
                pageSize,
                showFolderList,
                showAllFolders
            })
        );
    }, [chosenFolderId, searchText, committedSearch, pageIndex, pageSize, showFolderList, showAllFolders]);

    const renderFolderActions = (folder, closeMenu) => (
        <div
            className="folder-actions-dropdown"
            ref={folderActionsRef}
            style={{
                position: "absolute",
                top: 28,
                right: -4,
                background: "white",
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: 6,
                zIndex: 100,
                width: 120,
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
            }}
        >
            <div
                className="action-item"
                style={{ padding: 6, cursor: "pointer" }}
                onClick={() => {
                    openRenamePopup(folder);
                    closeMenu();
                }}
            >
                Rename
            </div>

            <div
                className="action-item"
                style={{ padding: 6, cursor: "pointer", color: "red" }}
                onClick={() => {
                    openDeleteConfirm(folder);
                    closeMenu();
                }}
            >
                Delete
            </div>
        </div>
    );

    return (
        <Container fluid className="curated-container-styles" style={{ minHeight: "400px", padding: 12 }}>
            <div className={`curated-landing-page-folder-section ${showFolderList ? "folders-expanded" : ""}`}>
                <div
                    className="curated-landing-page-folder-left"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                        setShowFolderList((s) => !s);
                        setShowAllFolders(false);
                        setChosenFolderId(null);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && setChosenFolderId(null)}
                >
                    <span className={`curated-landing-page-folder-label ${chosenFolderId === null ? "active" : ""}`}>
                        All Folders
                    </span>
                </div>

                {showFolderList && (
                    <div className="curated-folder-tabs" style={{ marginLeft: 16 }}>
                        {displayedFolders.map((folder) => (
                            <div
                                key={folder.folderId}
                                className={`curated-folder-tab-wrapper`}
                                style={{ position: "relative" }}
                            >
                                <div
                                    className={`curated-folder-tab ${chosenFolderId === folder.folderId ? "active" : ""}`}
                                    onClick={() => {
                                        setChosenFolderId(folder.folderId);
                                        setShowFolderActions(null);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {folder.name}
                                </div>

                                <div
                                    className="folder-actions-trigger"
                                    style={{
                                        position: "absolute",
                                        right: -12,
                                        top: 4,
                                        cursor: "pointer",
                                        fontSize: 16,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowFolderActions((cur) =>
                                            cur === folder.folderId ? null : folder.folderId
                                        );
                                        setFolderToModify(folder);
                                    }}
                                >
                                    ⋮
                                </div>
                                {showFolderActions === folder.folderId &&
                                    renderFolderActions(folder, () => setShowFolderActions(null))
                                }
                            </div>
                        ))}

                        {additionalFolders.length > 0 && (
                            <div className="curated-view-all-wrapper" ref={viewAllRef}>
                                <button
                                    type="button"
                                    className="curated-view-all-button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowAllFolders((s) => !s);
                                    }}
                                >
                                    View All
                                </button>
                                {showAllFolders && (

                                    <div className="curated-extra-folder-dropdown">

                                        {additionalFolders.map((folder) => (
                                            <div
                                                key={folder.folderId}
                                                className="curated-folder-dropdown-row"
                                            >
                                                <span
                                                    className={`curated-folder-name ${chosenFolderId === folder.folderId ? "active" : ""
                                                        }`}
                                                    onClick={() => {
                                                        setChosenFolderId(folder.folderId);
                                                        setShowAllFolders(false);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    {folder.name}
                                                </span>

                                                <div className="folder-actions-menu">
                                                    <button
                                                        type="button"
                                                        className="folder-actions-trigger"
                                                        onClick={() =>
                                                            setActiveFolderMenu(
                                                                activeFolderMenu === folder.folderId
                                                                    ? null
                                                                    : folder.folderId
                                                            )
                                                        }
                                                    >
                                                        ⋮
                                                    </button>
                                                    {activeFolderMenu === folder.folderId &&
                                                        renderFolderActions(folder, () => setActiveFolderMenu(null))
                                                    }

                                                </div>
                                            </div>
                                        ))}
                                        <div className="folder-pagination">
                                            <button type="button"
                                                disabled={folderPage === 0}
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    const prev = folderPage - 1;

                                                    setShowAllFolders(true);
                                                    setFolderPage(prev);

                                                    fetchFolders(prev);
                                                }}
                                            >
                                                ‹
                                            </button>

                                            {Array.from({ length: folderPageCount }, (_, idx) => (
                                                <button type="button"
                                                    key={idx}
                                                    className={folderPage === idx ? "active" : ""}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowAllFolders(true);

                                                        setFolderPage(idx);
                                                        fetchFolders(idx);
                                                    }}
                                                >
                                                    {idx + 1}
                                                </button>
                                            ))}

                                            <button type="button"
                                                disabled={folderPage + 1 >= folderPageCount}
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    const next = folderPage + 1;

                                                    setShowAllFolders(true);
                                                    setFolderPage(next);

                                                    fetchFolders(next);
                                                }}
                                            >
                                                ›
                                            </button>
                                        </div>

                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={{ marginTop: 12 }}>
                <div className="entry-info-search" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span> Showing {startEntry} to {endEntry} of {totalEntries} entries </span>

                    <div className="curated-candidate-search-field" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                            type="text"
                            placeholder="Search By Name"
                            value={searchText}
                            onChange={(evt) => setSearchText(evt.target.value)}
                            onKeyDown={(evt) => {
                                if (evt.key === "Enter") {
                                    runSearchCommit(evt.target.value);
                                }
                            }}
                            spellCheck={false}
                            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd" }}
                        />

                        <FontAwesomeIcon
                            icon={faSearch}
                            className="search-icon"
                            style={{ cursor: "pointer" }}
                            onClick={() => runSearchCommit(searchText)}
                        />
                    </div>
                </div>
            </div>

            {showRenamePopup && (
                <div className="rename-popup-overlay">
                    <div className="rename-popup">
                        <h4>Rename Folder</h4>

                        <input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            style={{ width: "100%", padding: 8 }}
                        />

                        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowRenamePopup(false);
                                    setFolderToRename(null);
                                }} style={{ padding: "6px 14px", borderRadius: '100px', }}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                style={{ padding: "6px 14px", background: "#F35824", borderRadius: '100px', color: "white" }}
                                onClick={async () => {
                                    try {
                                        await ClientFolderService.renameFolder(
                                            clientId,
                                            folderToRename.folderId,
                                            renameValue
                                        );

                                        await fetchFolders();
                                        setShowRenamePopup(false);
                                    } catch (err) {
                                        console.error("Rename failed", err);
                                    }
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showDeletePopup && (
                <div className="delete-popup-overlay">
                    <div className="delete-popup">
                        <h4>Delete Folder</h4>
                        <p>Are you sure you want to delete "{folderToDelete?.name}"?</p>

                        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowDeletePopup(false);
                                    setFolderToDelete(null);
                                }} style={{ padding: "6px 14px", borderRadius: '100px' }}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                style={{ padding: "6px 14px", background: "red", color: "white", borderRadius: '100px' }}
                                onClick={async () => {
                                    try {
                                        await ClientFolderService.deleteFolder(
                                            folderToDelete.folderId,
                                            clientId
                                        );

                                        if (folderToDelete.folderId === chosenFolderId) {
                                            setChosenFolderId(null);
                                        }

                                        setShowDeletePopup(false);
                                    } catch (err) {
                                        console.error("Delete failed", err);
                                    }
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showUncuratePopup && (
                <div className="folder-modal-overlay">
                    <div className="folder-modal">
                        <h4>Uncurate Candidate</h4>
                        <p>
                            Are you sure you want to uncurate this candidate?
                        </p>

                        <div className="modal-actions">
                            <button type="button"
                                onClick={() => {
                                    setShowUncuratePopup(false);
                                    setCandidateToUncurate(null);
                                }}
                            >
                                Cancel
                            </button>

                            <button type="button"
                                style={{ background: "red", color: "#fff" }}
                                onClick={handleUncurateConfirm}
                                disabled={uncurateLoading}
                            >
                                {uncurateLoading ? "Uncurating..." : "Yes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showUncurateSuccess && (
                <div className="folder-modal-overlay">
                    <div className="folder-modal">
                        <p>Uncurate successful</p>

                        <div className="modal-actions">
                            <button
                                onClick={() => setShowUncurateSuccess(false)}
                                style={{ background: "#F35824", color: "#fff" }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Row style={{ marginTop: 12 }}>
                <Col>
                    {isFetchingCandidates ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <CuratedTables
                            key={chosenFolderId || "all"}
                            fetchData={tableFetchData}
                            pageIndex={pageIndex}
                            setPageIndex={setPageIndex}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            committedSearch={committedSearch}
                            chosenFolderId={chosenFolderId}
                            tableRefresh={tableRefresh}
                        />
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default CuratedCandidates;